// Client-side PDF export: rasterise a DOM element with html2canvas and lay it
// out with jsPDF. Two formats share the same rendered data — only the target
// page geometry differs:
//   • A4  (exportElementToPdf)       — 210mm portrait, multi-column report.
//   • Mobile (exportMobileElementToPdf) — narrow 420pt-wide page(s) built from a
//     dedicated single-column phone layout, so text reads without zooming.
// Libraries are dynamically imported so they don't bloat the initial bundle.
//
// iOS Safari note: mobile Safari (and older Android Chrome) cap a single <canvas>
// at ~4096–8192px per side; anything taller silently rasterises to a BLANK bitmap,
// so a naive full-height html2canvas call produces an empty PDF on phones. To stay
// reliable everywhere we rasterise the element in vertical SLICES, each capped well
// under that limit, then stitch the slices back together in the PDF. Desktop is
// unaffected (it just gets a couple of slices instead of one).

async function loadLibs() {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
  ]);
  return { html2canvas, jsPDF };
}

// Max canvas height (in device px, i.e. AFTER scale) we allow per slice. iOS
// Safari's hard ceiling is device-dependent (4096 on older iPhones); 3000 leaves
// comfortable headroom on every device we care about.
const MAX_SLICE_CANVAS_PX = 3000;

// Rasterise `element` top-to-bottom into N slices, each a small iOS-safe canvas.
// Returns { slices, elW, elH } where each slice is { canvas, elY, elH } describing
// the element-space band (CSS px) it covers, so callers can reposition them.
async function rasteriseSlices(html2canvas, element, scale) {
  const elW = element.scrollWidth;
  const elH = element.scrollHeight;
  const sliceElH = Math.max(200, Math.floor(MAX_SLICE_CANVAS_PX / scale)); // element px per slice
  const slices = [];
  for (let y = 0; y < elH; y += sliceElH) {
    const h = Math.min(sliceElH, elH - y);
    // width/height crop the output canvas; x/y set the crop origin within the
    // element. windowWidth/Height keep the off-screen clone laid out at full size
    // so a slice renders identically to the full element (media queries, wrapping).
    const canvas = await html2canvas(element, {
      scale,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      windowWidth: elW,
      windowHeight: elH,
      width: elW,
      height: h,
      x: 0,
      y,
    });
    slices.push({ canvas, elY: y, elH: h });
  }
  return { slices, elW, elH };
}

// ---- A4 desktop report ----
// Fixed A4 pages; the full report flows across pages. Each slice is drawn at its
// absolute vertical position offset by the current page top — jsPDF clips anything
// past the page edges, so slices spanning a page break render correctly on both.
export async function exportElementToPdf(element, filename) {
  if (!element) return;
  const { html2canvas, jsPDF } = await loadLibs();
  const scale = 2;
  const { slices, elW, elH } = await rasteriseSlices(html2canvas, element, scale);

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();   // 210
  const pageH = pdf.internal.pageSize.getHeight();  // 297
  const margin = 8;
  const usableW = pageW - margin * 2;
  const usableH = pageH - margin * 2;
  const mmPerPx = usableW / elW;                    // element px -> mm at page width
  const totalH = elH * mmPerPx;                     // full report height in mm
  const pageCount = Math.max(1, Math.ceil(totalH / usableH));

  for (let p = 0; p < pageCount; p++) {
    if (p > 0) pdf.addPage();
    const pageTopMm = p * usableH;                  // absolute mm of this page's top
    for (const s of slices) {
      const sliceTopMm = s.elY * mmPerPx;
      const sliceHmm = s.elH * mmPerPx;
      // skip slices that don't touch this page
      if (sliceTopMm + sliceHmm < pageTopMm || sliceTopMm > pageTopMm + usableH) continue;
      const y = margin + (sliceTopMm - pageTopMm);
      pdf.addImage(s.canvas.toDataURL('image/jpeg', 0.92), 'JPEG', margin, y, usableW, sliceHmm);
    }
  }

  pdf.save(filename);
}

// ---- Mobile report (narrow, phone-optimised) ----
// The source element is a dedicated single-column layout at a fixed narrow width,
// mapped onto a 420pt-wide page. Normal reports fit ONE tall page (best phone
// reading — continuous scroll, no cuts); unusually long ones fall back to one page
// per slice so we never exceed the PDF page-size ceiling.
const MOBILE_PAGE_W = 420;              // pt
const MOBILE_MAX_SINGLE_H = 13000;      // pt — stay well under the 14400 PDF limit

export async function exportMobileElementToPdf(element, filename) {
  if (!element) return;
  const { html2canvas, jsPDF } = await loadLibs();
  const scale = 2;
  const { slices, elW, elH } = await rasteriseSlices(html2canvas, element, scale);

  const imgW = MOBILE_PAGE_W;
  const ptPerPx = imgW / elW;                       // element px -> pt at 420pt wide
  const totalH = elH * ptPerPx;                     // full-image height in pt

  if (totalH <= MOBILE_MAX_SINGLE_H) {
    // one tall page sized exactly to the content; slices stacked at their offsets
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: [imgW, totalH] });
    for (const s of slices) {
      const y = s.elY * ptPerPx;
      const h = s.elH * ptPerPx;
      pdf.addImage(s.canvas.toDataURL('image/jpeg', 0.95), 'JPEG', 0, y, imgW, h);
    }
    pdf.save(filename);
    return;
  }

  // very long report: one page per slice (natural page breaks)
  let pdf;
  for (let i = 0; i < slices.length; i++) {
    const s = slices[i];
    const h = s.elH * ptPerPx;
    if (i === 0) pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: [imgW, h] });
    else pdf.addPage([imgW, h]);
    pdf.addImage(s.canvas.toDataURL('image/jpeg', 0.95), 'JPEG', 0, 0, imgW, h);
  }
  pdf.save(filename);
}

// {ProjectName}_{UnitType}_Analysis_{DD-MM-YYYY}[_A4|_Mobile].pdf
export function pdfFilename(projectName, unitType, variant) {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yyyy = now.getFullYear();
  const clean = (s) => (s || '').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '');
  const suffix = variant ? `_${variant}` : '';
  return `${clean(projectName)}_${clean(unitType)}_Analysis_${dd}-${mm}-${yyyy}${suffix}.pdf`;
}
